import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, CreditCard, Gift, Sparkles, Wallet, AlertCircle, Clock, MessageSquare } from 'lucide-react';
import { GuestBottomNav } from '../components/GuestBottomNav';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { clearActiveGuestInviteId, getActiveGuestInviteId } from '../utils/deepLinkHandler';
import { formatEuropeanDate, formatEuropeanTime } from '../utils/dateFormat';
import WelcomeTourSheet from '../components/WelcomeTourSheet';
import PageInfoButton from '../components/PageInfoButton';
import { guestQrTourSlides } from '../components/welcomeTourSlides';
import { hasSeenWelcomeTour, markWelcomeTourSeen } from '../utils/welcomeTourState';
import GuestQREmptyState from '../components/guest/GuestQREmptyState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

export default function GuestQRPage() {
  const navigate = useNavigate();
  const [allPasses, setAllPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [paymentMethodConnected, setPaymentMethodConnected] = useState(null); // null=unknown, true/false
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenWelcomeTour('guest-page-qr')) {
      setTourOpen(true);
    }
  }, []);

  const handleCloseTour = () => {
    setTourOpen(false);
    markWelcomeTourSeen('guest-page-qr');
  };

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGuestPass = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/guest-passes/my`, { headers });
      if (res.data && res.data.length > 0) {
        setAllPasses(res.data);
        clearActiveGuestInviteId();
      }
    } catch (err) {
      // network / auth fail — leave allPasses empty
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentMethod = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/guest/payment-method`, { headers });
      setPaymentMethodConnected(Boolean(res.data?.connected));
    } catch (err) {
      setPaymentMethodConnected(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPendingTransaction = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/transactions/pending`, { headers });
      if (res.data.pending) {
        const pendingTxnId = res.data.transaction.id;

        // If the guest just cancelled this specific transaction, don't
        // auto-redirect back. Consume the flag so the NEXT new pending
        // transaction (from a fresh venue scan) will redirect normally.
        const suppressed = sessionStorage.getItem('allure.suppressPendingRedirect');
        if (suppressed === pendingTxnId) {
          sessionStorage.removeItem('allure.suppressPendingRedirect');
          return;
        }

        // Only auto-jump to the payment screen when the guest has a saved
        // card. If they don't, keep them on the QR page so the "Payment
        // method not connected" banner is visible and actionable.
        const pmRes = await axios.get(`${API}/guest/payment-method`, { headers });
        if (pmRes.data?.connected) {
          navigate(`/guest/payment/${pendingTxnId}`);
        } else {
          setPaymentMethodConnected(false);
        }
      }
    } catch (err) {
      // ignore
    }
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch (e) { /* noop */ }
    }
    fetchGuestPass();
    fetchPaymentMethod();
    checkPendingTransaction();
    const interval = setInterval(checkPendingTransaction, 5000);
    return () => clearInterval(interval);
  }, [fetchGuestPass, fetchPaymentMethod, checkPendingTransaction]);

  // Derive the primary pass to display:
  // 1. Active passes first (already sorted by active_from asc from backend)
  // 2. If no active pass, show earliest scheduled (pending) pass
  const now = new Date();
  const activePasses = allPasses.filter(p => {
    const af = p.active_from ? new Date(p.active_from) : null;
    const ea = p.expires_at ? new Date(p.expires_at) : null;
    return (!af || af <= now) && (!ea || ea > now);
  });
  const scheduledPasses = allPasses.filter(p => {
    const af = p.active_from ? new Date(p.active_from) : null;
    return af && af > now;
  });
  const hasActivePass = activePasses.length > 0;
  const guestPass = hasActivePass ? activePasses[0] : (scheduledPasses[0] || allPasses[0] || null);

  const activeFromDate = guestPass?.active_from ? new Date(guestPass.active_from) : null;
  const expiresAtDate = guestPass?.expires_at ? new Date(guestPass.expires_at) : null;

  // Re-evaluate isPendingActivation every 30s so the UI flips to Active
  // when the countdown reaches zero without a full page refresh.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!activeFromDate || activeFromDate <= new Date()) return;
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [activeFromDate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="app-shell" style={{ backgroundColor: 'var(--surface)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src="/allure-logo.png" alt="" style={{ width: 80, height: 71, objectFit: 'contain' }} className="animate-pulse" />
        <GuestBottomNav />
      </div>
    );
  }

  // ========== STATE: No QR Code yet ==========
  if (!guestPass || allPasses.length === 0) {
    const activeInviteId = getActiveGuestInviteId();
    if (activeInviteId) {
      navigate(`/invite/${activeInviteId}/waiting`, { replace: true });
      return null;
    }
    return <GuestQREmptyState navigate={navigate} tourOpen={tourOpen} setTourOpen={setTourOpen} handleCloseTour={handleCloseTour} />;
  }

  // ========== STATE: Has QR Code — Boarding Pass design ==========
  const guestFirstName = (guestPass.guest_name || user?.name || 'Guest').split(' ')[0];
  const hostName = guestPass.host_name || 'your host';
  const discountPct = Number(guestPass.current_discount || 8);
  const totalSaved = Number(guestPass.total_saved || 0);

  // Show countdown only when this pass is scheduled AND there is NO other
  // active pass. This prevents showing countdown on a future pass when a
  // current pass is still active.
  const isPendingActivation = !hasActivePass && activeFromDate && activeFromDate > new Date();
  const customerId = (user?.customer_id || user?.id?.slice(0, 6).toUpperCase() || 'XXXXXX').slice(0, 6);

  const statusLabel = isPendingActivation ? 'Activates soon' : 'Active';
  const statusColor = isPendingActivation ? 'var(--accent-purple)' : '#1F8E2A';
  const statusBgColor = isPendingActivation ? 'var(--accent-surface)' : 'rgba(31, 142, 42, 0.10)';

  return (
    <div className="app-shell" data-testid="guest-qr-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Top header: info button (left) + bell (right) */}
      <div style={{ padding: '14px 18px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageInfoButton
          onClick={() => setTourOpen(true)}
          testId="guest-qr-page-info-button"
        />
        <NotificationBellButton buttonTestId="guest-qr-bell-button" popupTestId="guest-qr-notification-popup" />
      </div>

      <div
        className="page-content"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 0,
          paddingBottom: 110,
        }}
      >
        <RouteTransitionLogo width={104} height={94} dataTestId="guest-qr-shared-logo" />

        {/* Payment method not connected banner */}
        {paymentMethodConnected === false ? (
          <div
            data-testid="guest-qr-payment-method-banner"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/guest/settings/payment-method')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/guest/settings/payment-method'); }}
            style={{
              width: 'calc(100% - 40px)',
              maxWidth: 360,
              marginTop: 4,
              marginBottom: 8,
              padding: '12px 14px',
              borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(229, 57, 53, 0.10) 0%, rgba(255, 152, 0, 0.10) 100%)',
              border: '1px solid rgba(229, 57, 53, 0.28)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'var(--surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AlertCircle size={20} color="#C92016" strokeWidth={2} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                Payment method not connected
              </div>
              <div style={{ fontFamily: FONT, fontSize: 12, color: 'var(--text-2)', marginTop: 1, lineHeight: 1.4 }}>
                Add a card so you can pay at any partner venue with one tap.
              </div>
            </div>
            <ArrowRight size={18} color="#C92016" strokeWidth={2} />
          </div>
        ) : null}

        {/* Boarding Pass card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          data-testid="guest-qr-pass-card"
          style={{
            width: 'calc(100% - 40px)',
            maxWidth: 360,
            marginTop: 10,
            background: 'var(--surface)',
            borderRadius: 26,
            border: '1px solid var(--card-border)',
            boxShadow: 'var(--premium-shadow-main)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* soft holographic top wash */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 110,
              background:
                'linear-gradient(135deg, var(--accent-surface) 0%, rgba(229, 57, 53, 0.06) 50%, rgba(255, 200, 100, 0.04) 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* Eyebrow row */}
          <div style={{ position: 'relative', padding: '18px 22px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontFamily: FONT, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 700 }}>
                Allure pass
              </span>
              <span data-testid="guest-qr-name" style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)', marginTop: 2 }}>
                {guestFirstName}
              </span>
            </div>
            <span
              data-testid="guest-qr-status-pill"
              style={{
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: 700,
                color: statusColor,
                background: statusBgColor,
                padding: '5px 11px',
                borderRadius: 9999,
                whiteSpace: 'nowrap',
              }}
            >
              {statusLabel}
            </span>
          </div>

          {paymentMethodConnected === true ? (
            <div
              data-testid="guest-qr-saved-card-pill"
              style={{
                position: 'relative',
                width: 'calc(100% - 44px)',
                margin: '12px auto 0',
                padding: '8px 12px',
                borderRadius: 9999,
                background: 'rgba(31, 142, 42, 0.10)',
                border: '1px solid rgba(31, 142, 42, 0.18)',
                color: '#1F8E2A',
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
              }}
            >
              <CreditCard size={15} strokeWidth={2.3} />
              Saved card — just tap Pay
            </div>
          ) : null}

          {/* QR area */}
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: paymentMethodConnected === true ? '12px 22px 0' : '14px 22px 0' }}>
            {/*
              QR quiet-zone wrapper MUST stay WHITE regardless of the
              guest's theme. QR spec requires ~4 modules of light
              padding around the code for finder-pattern detection —
              in dark mode `var(--surface)` was dark, swallowing the
              quiet zone and making the pass unscannable from a host
              phone. Hard-coding white here restores the quiet zone
              in both themes without affecting the surrounding card.
            */}
            <div
              data-testid="guest-qr-code"
              style={{
                position: 'relative',
                width: 230,
                height: 230,
                borderRadius: 22,
                background: '#FFFFFF',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: 'var(--premium-shadow-nested)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <QrCodeDisplay value={guestPass.qr_code_data} />
              {isPendingActivation && activeFromDate && (
                <ActivationCountdown activeFromDate={activeFromDate.getTime()} />
              )}
              {/* Discount stamp on the QR */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -18,
                  right: -10,
                  background: '#1F8E2A',
                  color: '#FFFFFF',
                  fontFamily: FONT,
                  fontSize: 14,
                  fontWeight: 700,
                  padding: '6px 12px',
                  borderRadius: 9999,
                  boxShadow: '0 6px 14px rgba(31, 142, 42, 0.32)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  zIndex: 2,
                }}
              >
                <Gift size={13} strokeWidth={2.4} />
                {discountPct}% OFF
              </div>
            </div>
          </div>

          {/* Customer ID + dotted ticket separator */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px 0' }}>
            <span style={{ fontFamily: FONT, fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 700 }}>
              Pass id
            </span>
            <span data-testid="guest-qr-customer-id" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, color: 'var(--text)', letterSpacing: 1.5 }}>
              {customerId}
            </span>
          </div>

          <div aria-hidden style={{ position: 'relative', margin: '14px 0 0' }}>
            <span style={{ position: 'absolute', left: -10, top: -8, width: 16, height: 16, borderRadius: '50%', background: 'var(--app-bg)' }} />
            <span style={{ position: 'absolute', right: -10, top: -8, width: 16, height: 16, borderRadius: '50%', background: 'var(--app-bg)' }} />
            <div style={{ borderTop: '2px dashed var(--card-border)', margin: '0 18px' }} />
          </div>

          {/* Stat rows */}
          <div style={{ padding: '14px 22px 18px', display: 'grid', gap: 10 }}>
            <StatRow
              label={isPendingActivation ? 'Activates' : 'Valid until'}
              value={
                isPendingActivation && activeFromDate
                  ? `${formatEuropeanTime(guestPass.active_from)} · ${formatEuropeanDate(guestPass.active_from)}`
                  : expiresAtDate
                  ? `${formatEuropeanTime(guestPass.expires_at)} · ${formatEuropeanDate(guestPass.expires_at)}`
                  : '—'
              }
              testId="guest-qr-stat-window"
            />
            <StatRow
              label="Hosted by"
              value={hostName}
              testId="guest-qr-stat-host"
              valueColor="var(--accent-purple-strong)"
            />
            {hasActivePass && guestPass.conversation_id ? (
              <button
                type="button"
                data-testid="guest-qr-message-host"
                onClick={() => navigate(`/guest/chat/${guestPass.conversation_id}`)}
                style={{
                  marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 8, padding: '10px 0', borderRadius: 9999,
                  border: '1px solid var(--card-border)', background: 'var(--surface)',
                  fontFamily: FONT, fontSize: 14, fontWeight: 700,
                  color: 'var(--accent-purple-strong)', cursor: 'pointer',
                }}
              >
                <MessageSquare size={15} strokeWidth={2.2} /> Message {hostName}
              </button>
            ) : null}
          </div>
        </motion.div>

        {/* Bottom info chips */}
        <div
          style={{
            width: 'calc(100% - 40px)',
            maxWidth: 360,
            marginTop: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {totalSaved > 0 ? (
            <div
              data-testid="guest-qr-saved-chip"
              style={{
                background: 'var(--surface)',
                border: '1px solid rgba(31, 142, 42, 0.20)',
                borderRadius: 16,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  background: 'rgba(31, 142, 42, 0.10)',
                  color: '#1F8E2A',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Sparkles size={17} strokeWidth={2.2} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: FONT, fontSize: 13, color: 'var(--text-2)' }}>You've saved this stay</p>
                <p style={{ margin: 0, fontFamily: FONT, fontSize: 18, fontWeight: 700, color: '#1F8E2A' }}>
                  €{totalSaved.toFixed(2)}
                </p>
              </div>
            </div>
          ) : null}

          <div
            data-testid="guest-qr-payment-info"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              borderRadius: 16,
              padding: '12px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 12,
                background: 'var(--accent-surface)',
                color: 'var(--accent-purple)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CreditCard size={17} strokeWidth={2.2} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontFamily: FONT, fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                Pay through Allure at checkout
              </p>
              <p style={{ margin: 0, fontFamily: FONT, fontSize: 12, color: 'var(--text-2)' }}>
                Card · Apple Pay · Google Pay — when the venue rings up your bill
              </p>
            </div>
          </div>

          <button
            type="button"
            data-testid="guest-qr-find-venues-cta"
            onClick={() => navigate('/guest/venues')}
            style={{
              marginTop: 4,
              borderRadius: 9999,
              padding: '13px 18px',
              border: 'none',
              background: 'var(--cta-primary-bg)',
              color: 'var(--cta-primary-text)',
              fontFamily: FONT,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: 'var(--premium-shadow-main)',
            }}
          >
            Find Allure venues nearby
            <ArrowRight size={17} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      <GuestBottomNav />

      <WelcomeTourSheet
        open={tourOpen}
        onClose={handleCloseTour}
        slides={guestQrTourSlides}
        testIdPrefix="guest-qr-page-tour"
      />
    </div>
  );
}

function StatRow({ label, value, testId, valueColor = 'var(--text)' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span style={{ fontFamily: FONT, fontSize: 13, color: 'var(--text-2)', flexShrink: 0 }}>{label}</span>
      <span
        data-testid={testId}
        style={{
          fontFamily: FONT,
          fontSize: 14,
          fontWeight: 700,
          color: valueColor,
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 220,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function QrCodeDisplay({ value }) {
  // Dynamically import qrcode.react
  // eslint-disable-next-line global-require
  const { QRCodeSVG } = require('qrcode.react');
  return (
    <QRCodeSVG
      value={value || 'ALLURE'}
      size={194}
      level="H"
      bgColor="#FFFFFF"
      fgColor="#000000"
      style={{ display: 'block' }}
    />
  );
}

function ActivationCountdown({ activeFromDate }) {
  const [remaining, setRemaining] = useState(() => Math.max(0, activeFromDate - Date.now()));

  useEffect(() => {
    const tick = setInterval(() => {
      const diff = Math.max(0, activeFromDate - Date.now());
      setRemaining(diff);
      if (diff <= 0) clearInterval(tick);
    }, 1000);
    return () => clearInterval(tick);
  }, [activeFromDate]);

  if (remaining <= 0) return null;

  const totalSec = Math.floor(remaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  const pad = (n) => String(n).padStart(2, '0');

  let display;
  if (days > 0) {
    display = `${days}d ${pad(hours)}h ${pad(mins)}m`;
  } else {
    display = `${pad(hours)}h ${pad(mins)}m ${pad(secs)}s`;
  }

  return (
    <div
      data-testid="guest-qr-activation-countdown"
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 22,
        background: 'rgba(14, 4, 25, 0.88)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        zIndex: 3,
      }}
    >
      <Clock size={22} color="var(--accent-purple)" strokeWidth={2} />
      <span style={{
        fontFamily: FONT,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: 'var(--text-2)',
      }}>
        Activates in
      </span>
      <span style={{
        fontFamily: "'ui-monospace', 'SFMono-Regular', 'Menlo', monospace",
        fontSize: 28,
        fontWeight: 700,
        color: '#FFFFFF',
        letterSpacing: 2,
      }}>
        {display}
      </span>
    </div>
  );
}
